import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { InputField } from '../../../../../../../../components/Form/components/InputField/InputField';
import { Error } from '../../../../../../../../components/Form/Form.styles';
import { useLogService } from '../../../../../../../../hooks/useLogService';
import { useTranslate } from '../../../../../../../../hooks/useTranslate';
import { useMarket } from '../../../../../../../../providers/MarketProvider';
import { useSembonusUser } from '../../../../../../../../providers/UserProvider';
import {
  type LogInFormFields,
  performLogIn,
} from '../../../../../../../../utils/auth';
import {
  ButtonsWrapper,
  FieldsWrapper,
  StyledButton,
  StyledForm,
} from '../../AuthModal.styles';
import { ForgotPasswordButton } from './LogInForm.styles';

type Props = {
  onSuccess: (travelAgencyId: string | null) => void;
  onRegister: () => void;
  onForgottenPassword: () => void;
  onFieldDirty?: () => void;
};

export const LogInForm = ({
  onSuccess,
  onRegister,
  onForgottenPassword,
  onFieldDirty,
}: Props) => {
  const { t } = useTranslate();
  const market = useMarket();
  const { checkLoginStatus } = useSembonusUser();
  const { logError } = useLogService();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<LogInFormFields>();

  const onSubmit = async (data: LogInFormFields) => {
    setIsLoading(true);
    setIsError(false);

    try {
      const response = await performLogIn(data);

      if (!response.ok) {
        setIsError(true);
        return;
      }

      const loginState = await checkLoginStatus();

      return onSuccess(loginState?.userInfo?.member?.travelAgencyId ?? null);
    } catch (loginError) {
      setIsError(true);
      logError(loginError, {
        extra: {
          market,
          location: 'share-widget',
          username: data.username,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (onFieldDirty && Object.keys(dirtyFields).length > 0) {
      onFieldDirty();
    }
  }, [dirtyFields, onFieldDirty]);

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <FieldsWrapper>
        <InputField
          htmlId="share-widget-log-in-username"
          inputMode="email"
          label={t('ui.action.logIn.username.label')}
          placeholder={t('ui.action.logIn.username.placeholder')}
          error={errors.username?.message}
          {...register('username', {
            required: t('ui.action.logIn.username.requiredError'),
          })}
        />

        <InputField
          htmlId="share-widget-log-in-password"
          type="password"
          label={t('ui.action.logIn.password.label')}
          placeholder={t('ui.action.logIn.password.placeholder')}
          error={errors.password?.message}
          {...register('password', {
            required: t('ui.action.logIn.password.requiredError'),
          })}
        />

        {isError && <Error>{t('ui.action.logIn.genericError')}</Error>}

        <ForgotPasswordButton type="button" onClick={onForgottenPassword}>
          {t('ui.action.logIn.forgotenPassword')}
        </ForgotPasswordButton>
      </FieldsWrapper>

      <ButtonsWrapper>
        <StyledButton
          type="submit"
          variant="primary"
          size="large"
          disabled={isLoading}
        >
          {isLoading
            ? t('ui.action.logIn.submitButton.loading')
            : t('ui.action.logIn.submitButton.label')}
        </StyledButton>

        <StyledButton variant="secondary" size="large" onClick={onRegister}>
          {t('ui.action.logIn.register')}
        </StyledButton>
      </ButtonsWrapper>
    </StyledForm>
  );
};
